import { combineEpics, ofType } from 'redux-observable'
import { concat, EMPTY, of } from 'rxjs'
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators'

import { addToast } from 'containers/Toasts/actions'

import {
  createDestinationError,
  createDestinationSuccess,
  deactivateDestinationDone,
  deactivateDestinationLoading,
  deleteDestinationError,
  deleteDestinationSuccess,
  fetchDestination,
  fetchDestinationError,
  fetchDestinations,
  fetchDestinationsError,
  fetchDestinationsSuccess,
  fetchDestinationSuccess,
  updateDestinationError,
  updateDestinationSuccess,
} from './actions'
import {
  CREATE_DESTINATION_ASYNC,
  DEACTIVATE_DESTINATION,
  DELETE_DESTINATION_ASYNC,
  FETCH_DESTINATION_ASYNC,
  FETCH_DESTINATIONS_ASYNC,
  findDuplicateDestination,
  OPEN_DRAWER,
  UPDATE_DESTINATION_ASYNC,
} from './constants'
import { selectDestinations } from './selectors'

const DUPLICATE_DESTINATION_MESSAGE = 'This destination type already exists'

function buildDeactivateBody(dest) {
  const base = {
    status: 'disabled',
    logFormat: dest.logFormat,
    protocol: dest.protocol,
    port: dest.port ?? null,
    domain: dest.domain || null,
    ipAddress: dest.ipAddress || null,
    accessToken: dest.accessToken || null,
    allowedEventTypes: dest.allowedEventTypes,
  }
  const pc = dest.protocolConfiguration
  if (!pc) return base
  if (pc.kind === 'splunk') {
    return {
      ...base,
      protocolConfiguration: {
        kind: 'splunk',
        cloudIndex: pc.cloudIndex,
        hecTokenValue: pc.hecTokenValue,
        verifySsl: pc.verifySsl,
      },
    }
  }
  if (pc.kind === 'tls') {
    return {
      ...base,
      protocolConfiguration: {
        kind: 'tls',
        tlsCertificate: pc.tlsCertificate,
        disableServerIdentityValidation: false,
      },
    }
  }
  if (pc.kind === 'webhook') {
    return {
      ...base,
      protocolConfiguration: {
        kind: 'webhook',
        url: pc.url,
        authMethod: pc.authMethod,
        authHeaderName: pc.authHeaderName,
        authHeaderValue: pc.authHeaderValue,
      },
    }
  }
  return base
}

const fetchDestinationsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_DESTINATIONS_ASYNC.INITIATE),
    mergeMap(() =>
      api.settings.listDestinations(state$.value.partnerOptions.selectedCompany?.value).pipe(
        map((response) => fetchDestinationsSuccess(response)),
        catchError((error) => of(epicsErrorHandler(fetchDestinationsError, error))),
        takeUntil(action$.pipe(ofType(FETCH_DESTINATIONS_ASYNC.CANCEL)))
      )
    )
  )

const openDrawerEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(OPEN_DRAWER),
    mergeMap((action) => {
      const { destinationId } = action.payload
      if (!destinationId) return EMPTY
      return of(fetchDestination(destinationId))
    })
  )

const fetchDestinationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_DESTINATION_ASYNC.INITIATE),
    mergeMap((action) =>
      api.settings
        .getDestination(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.destinationId
        )
        .pipe(
          map((response) => fetchDestinationSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchDestinationError, error))),
          takeUntil(action$.pipe(ofType(FETCH_DESTINATION_ASYNC.CANCEL)))
        )
    )
  )

const createDestinationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_DESTINATION_ASYNC.INITIATE),
    mergeMap((action) => {
      const destination = action.payload.destination
      if (findDuplicateDestination(selectDestinations(state$.value), destination)) {
        return of(
          createDestinationError({ message: DUPLICATE_DESTINATION_MESSAGE }),
          addToast({ type: 'error', message: DUPLICATE_DESTINATION_MESSAGE })
        )
      }

      return api.settings
        .createDestination(state$.value.partnerOptions.selectedCompany?.value, destination)
        .pipe(
          mergeMap(() =>
            of(
              createDestinationSuccess(),
              fetchDestinations(),
              addToast({ type: 'success', message: 'Destination created' })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(createDestinationError, error),
              addToast({ type: 'error', message: 'Failed to create destination' })
            )
          ),
          takeUntil(action$.pipe(ofType(CREATE_DESTINATION_ASYNC.CANCEL)))
        )
    })
  )

const updateDestinationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_DESTINATION_ASYNC.INITIATE),
    mergeMap((action) => {
      const { destinationId, destination } = action.payload
      if (findDuplicateDestination(selectDestinations(state$.value), destination, destinationId)) {
        return of(
          updateDestinationError({ message: DUPLICATE_DESTINATION_MESSAGE }),
          addToast({ type: 'error', message: DUPLICATE_DESTINATION_MESSAGE })
        )
      }

      return api.settings
        .updateDestination(
          state$.value.partnerOptions.selectedCompany?.value,
          destinationId,
          destination
        )
        .pipe(
          mergeMap(() =>
            of(
              updateDestinationSuccess(),
              fetchDestinations(),
              addToast({ type: 'success', message: 'Destination updated' })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(updateDestinationError, error),
              addToast({ type: 'error', message: 'Failed to update destination' })
            )
          ),
          takeUntil(action$.pipe(ofType(UPDATE_DESTINATION_ASYNC.CANCEL)))
        )
    })
  )

const deleteDestinationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_DESTINATION_ASYNC.INITIATE),
    mergeMap((action) =>
      api.settings
        .deleteDestination(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.destinationId
        )
        .pipe(
          mergeMap(() =>
            of(
              deleteDestinationSuccess(),
              fetchDestinations(),
              addToast({ type: 'success', message: 'Destination removed' })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(deleteDestinationError, error),
              addToast({ type: 'error', message: 'Failed to remove destination' })
            )
          ),
          takeUntil(action$.pipe(ofType(DELETE_DESTINATION_ASYNC.CANCEL)))
        )
    )
  )

const deactivateDestinationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DEACTIVATE_DESTINATION),
    mergeMap((action) => {
      const companyFilter = state$.value.partnerOptions.selectedCompany?.value
      return concat(
        of(deactivateDestinationLoading()),
        api.settings.getDestination(companyFilter, action.payload.destinationId).pipe(
          mergeMap((dest) =>
            api.settings
              .updateDestination(
                companyFilter,
                action.payload.destinationId,
                buildDeactivateBody(dest)
              )
              .pipe(
                mergeMap(() =>
                  of(
                    deactivateDestinationDone(),
                    fetchDestinations(),
                    addToast({ type: 'success', message: 'Destination deactivated' })
                  )
                )
              )
          ),
          catchError((error) =>
            of(
              deactivateDestinationDone(),
              addToast({ type: 'error', message: 'Failed to deactivate destination' })
            )
          )
        )
      )
    })
  )

export default combineEpics(
  fetchDestinationsEpic,
  openDrawerEpic,
  fetchDestinationEpic,
  createDestinationEpic,
  updateDestinationEpic,
  deleteDestinationEpic,
  deactivateDestinationEpic
)
